import {
  Card,
  ChannelIcon,
  Flex,
  Heading,
  IconButton,
  PencilIcon,
  Skeleton,
  TrashIcon,
  Tooltip,
  Text,
  VisuallyHidden,
  WarningIcon,
} from '@buffer-mono/popcorn'

import styles from './ChannelGroupItem.module.css'
import type {
  ChannelGroup_Channel,
  ChannelGroup_ChannelGroup,
} from '../hooks/useChannelGroups'
import { useVisibleElements } from '~publish/hooks/useVisibleElements'

// TODO: we will reuse this when we return to implement this feature

const GroupEmoji = ({ avatar }: { avatar: string }): JSX.Element => {
  return (
    <Flex
      align="center"
      justify="center"
      fullHeight
      className={styles.groupEmoji}
      aria-label={`Emoji for group`}
    >
      {avatar}
    </Flex>
  )
}

const ChannelList = ({
  channels,
}: {
  channels: ChannelGroup_Channel[]
}): JSX.Element => {
  const { containerRef, measurementRef, visibleCount, hiddenCount } =
    useVisibleElements({ minimumVisibleCount: 1 })

  const visibleChannels = channels.slice(0, visibleCount)

  return (
    <>
      <VisuallyHidden>
        <Flex
          ref={measurementRef}
          className={styles.channelListMeasurement}
          aria-hidden="true"
        >
          {channels.map((channel) => (
            <span
              key={`measure-${channel.id}`}
              className={styles.channelCardItem}
            >
              <ChannelIcon type={channel.service} color="branded" />
              <Text color="subtle">{channel.name}</Text>
            </span>
          ))}
          <span className={styles.remainingCount}>+0</span>
        </Flex>
      </VisuallyHidden>
      <Flex ref={containerRef} className={styles.channelList} fullWidth>
        <ul
          className={styles.channelList}
          aria-label={`Channels belonging to this group`}
        >
          {visibleChannels.map((channel) => (
            <li key={channel.id} className={styles.channelCardItem}>
              <ChannelIcon type={channel.service} color="branded" />
              <Text color="subtle">{channel.name}</Text>
            </li>
          ))}
          {hiddenCount > 0 && visibleChannels.length > 0 && (
            <li
              className={styles.remainingCount}
              aria-label={`${hiddenCount} more channels hidden`}
            >
              +{hiddenCount}
            </li>
          )}
        </ul>
      </Flex>
    </>
  )
}

const GroupDetails = ({
  channelGroup,
}: {
  channelGroup: ChannelGroup_ChannelGroup
}): JSX.Element => {
  return (
    <Flex
      as="article"
      direction="column"
      gap="2xs"
      fullWidth
      className={styles.groupDetails}
    >
      <Flex gap="2xs" align="center">
        <Heading as="h2" className={styles.groupName}>
          {channelGroup.name}
        </Heading>
      </Flex>
      <Skeleton show={!channelGroup.channels}>
        {channelGroup.channels && channelGroup.channels.length > 0 && (
          <ChannelList channels={channelGroup.channels} />
        )}
        {channelGroup.channels && channelGroup.channels.length === 0 && (
          <Flex
            direction="row"
            gap="2xs"
            align="center"
            className={styles.noChannels}
          >
            <WarningIcon size="small" color="subtle" />
            <Text color="subtle" size="sm">
              No channels in this group. This group will not be available in the
              composer.
            </Text>
          </Flex>
        )}
      </Skeleton>
    </Flex>
  )
}

const GroupActions = ({
  channelGroup,
  onEdit,
  onDelete,
}: {
  channelGroup: ChannelGroup_ChannelGroup
  onEdit: (channelGroup: ChannelGroup_ChannelGroup) => void
  onDelete: (channelGroup: ChannelGroup_ChannelGroup) => void
}): JSX.Element => {
  return (
    <Flex gap="2xs" className={styles.groupActions}>
      <Tooltip content="Edit channel group">
        <IconButton
          label="Edit"
          variant="tertiary"
          onClick={(): void => onEdit(channelGroup)}
          className={styles.groupActionsButton}
        >
          <PencilIcon />
        </IconButton>
      </Tooltip>
      <Tooltip content="Delete channel group">
        <IconButton
          label="Delete"
          variant="critical-subtle"
          onClick={(): void => onDelete(channelGroup)}
          className={styles.groupActionsButton}
        >
          <TrashIcon />
        </IconButton>
      </Tooltip>
    </Flex>
  )
}

const ChannelGroupItem = ({
  channelGroup,
  onEdit,
  onDelete,
}: {
  channelGroup: ChannelGroup_ChannelGroup
  onEdit: (channelGroup: ChannelGroup_ChannelGroup) => void
  onDelete: (channelGroup: ChannelGroup_ChannelGroup) => void
}): JSX.Element => {
  return (
    <Card className={styles.channelGroupItem}>
      {/* <GroupEmoji avatar={'🍊'} /> */}
      <GroupDetails channelGroup={channelGroup} />
      <GroupActions
        channelGroup={channelGroup}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </Card>
  )
}

export default ChannelGroupItem
